/**
 * Every team game opens the game menu's "Team play rules" modal by itself when
 * the player first enters it, showing which team they are on (maintainer,
 * 2026-11-03). Seen is recorded per user per game, so it opens once per game,
 * not on every visit. Stored through lib/storage (localStorage on web,
 * SecureStore on native).
 *
 * It never stacks with the ★ coach mark (lib/firstEntryNotice): the screen
 * holds the coach mark back while this notice is pending or showing.
 */
import React from 'react'
import { getItem, setItem } from './storage'
import { reportError } from './reporting'
import { FirstEntryNoticeEnabledContext } from './firstEntryNoticeContext'

/** SecureStore keys allow only [A-Za-z0-9._-]. */
export function teamRulesSeenKey(userId: string, gameId: string): string {
  const safe = (s: string) => s.replace(/[^A-Za-z0-9_-]/g, '_')
  return `teamRulesSeen.v2.${safe(userId)}.${safe(gameId)}`
}

export interface TeamRulesSeenStore {
  get: (key: string) => Promise<string | null>
  set: (key: string, value: string) => Promise<void>
}

const defaultStore: TeamRulesSeenStore = { get: getItem, set: setItem }

export async function hasSeenTeamRules(userId: string, gameId: string, store: TeamRulesSeenStore = defaultStore): Promise<boolean> {
  return (await store.get(teamRulesSeenKey(userId, gameId))) === '1'
}

export async function markTeamRulesSeen(userId: string, gameId: string, store: TeamRulesSeenStore = defaultStore): Promise<void> {
  await store.set(teamRulesSeenKey(userId, gameId), '1')
}

/**
 * `teamGame`: the viewer plays on a team in this live game (not a spectator,
 * not the snapshot view). `eligible`: nothing blocking is open right now.
 * `pending` stays true while storage is being read for a team game, so the
 * caller can hold the ★ coach mark back until this one is settled.
 */
export function useTeamRulesNotice({ userId, gameId, teamGame, eligible }: {
  userId: string | null | undefined
  gameId: string | null | undefined
  teamGame: boolean
  eligible: boolean
}) {
  const enabled = React.useContext(FirstEntryNoticeEnabledContext)
  const key = userId && gameId ? teamRulesSeenKey(userId, gameId) : null
  const [seen, setSeen] = React.useState<{ key: string; seen: boolean } | null>(null)
  React.useEffect(() => {
    if (!enabled || !teamGame || !key || !userId || !gameId) return
    let active = true
    hasSeenTeamRules(userId, gameId)
      .then((value) => { if (active) setSeen({ key, seen: value }) })
      .catch((error) => {
        reportError(error, { tags: { surface: 'team-rules-notice', action: 'read' } })
        if (active) setSeen({ key, seen: true })
      })
    return () => { active = false }
  }, [enabled, teamGame, key, userId, gameId])
  const markSeen = React.useCallback(() => {
    if (!key || !userId || !gameId) return
    setSeen({ key, seen: true })
    markTeamRulesSeen(userId, gameId).catch((error) => reportError(error, { tags: { surface: 'team-rules-notice', action: 'write' } }))
  }, [key, userId, gameId])
  const unseen = enabled && teamGame && !!key && (seen?.key !== key || !seen.seen)
  return { show: unseen && eligible && seen?.key === key, pending: unseen, markSeen }
}
